// src/pages/PanelFirma/PanelFirma.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Tabs, Tab, Badge, CircularProgress,
    IconButton, Tooltip, TextField, InputAdornment,
} from '@mui/material';
import {
    Description as DescriptionIcon,
    Gavel as GavelIcon,
    UploadFile as UploadFileIcon,
    People as PeopleIcon,
    Draw as DrawIcon,
    CheckCircle as CheckIcon,
    Cancel as CancelIcon,
    Schedule as ScheduleIcon,
    TrendingUp as TrendingUpIcon,
    History as HistoryIcon,
    Search as SearchIcon,
    Refresh as RefreshIcon,
    Warning as WarningIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import { panelFirmaAccesoJWT, panelFirmaStatsJWT } from '../../api/endpoints';
import { nodeApiPath } from '../../utils/paths';
import CartasPoder from './CartasPoder';
import FirmaCartasPoder from './FirmaCartasPoder';
import FirmaCargaMasiva from './FirmaCargaMasiva';
import Delegados from './Delegados';

// Paleta Panel Firma - gris azulado premium
const PF = {
    primary: '#37474f',
    primaryDark: '#263238',
    primaryLight: '#546e7a',
    accent: '#00897b',     // teal para acciones positivas
    accentDark: '#00695c',
    bg: '#eceff1',
    cardBg: '#fff',
    gold: '#ffa726',       // pendientes/urgentes
};

const PanelFirma = () => {
    const { enqueueSnackbar } = useSnackbar();

    const [loading, setLoading] = useState(true);
    const [acceso, setAcceso] = useState(null);
    const [stats, setStats] = useState(null);
    const [tabActual, setTabActual] = useState(0);

    const cargarAccesoYStats = useCallback(async () => {
        try {
            setLoading(true);
            const [accesoData, statsData] = await Promise.all([
                panelFirmaAccesoJWT(),
                panelFirmaStatsJWT(),
            ]);
            if (accesoData?.ok) setAcceso(accesoData);
            if (statsData?.ok) setStats(statsData);
        } catch (err) {
            enqueueSnackbar('Error al verificar acceso al panel', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    }, [enqueueSnackbar]);

    useEffect(() => {
        cargarAccesoYStats();
    }, [cargarAccesoYStats]);

    const refreshStats = useCallback(async () => {
        try {
            const statsData = await panelFirmaStatsJWT();
            if (statsData?.ok) setStats(statsData);
        } catch { /* ignore */ }
    }, []);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                <CircularProgress sx={{ color: PF.primary }} />
            </Box>
        );
    }

    if (!acceso || (!acceso.esFiscal && !acceso.esDelegado)) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: 400, gap: '16px' }}>
                <GavelIcon style={{ fontSize: 64, color: '#ccc' }} />
                <Typography variant="h5" color="text.secondary">
                    No tiene acceso al Panel de Firma
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Solo el Fiscal y sus delegados pueden acceder a este panel.
                </Typography>
            </Box>
        );
    }

    const pendientes = stats?.pendientes ?? 0;
    const firmadosHoy = stats?.firmadosHoy ?? 0;
    const rechazadosHoy = stats?.rechazadosHoy ?? 0;
    const totalHoy = firmadosHoy + rechazadosHoy;

    // Stat card component
    const StatCard = ({ icon, label, value, color, subtitle }) => (
        <Paper sx={{
            p: '16px 20px', borderRadius: '4px', display: 'flex', alignItems: 'center', gap: '16px',
            flex: 1, minWidth: 180, border: `1px solid ${color}22`, transition: 'all 0.2s',
            '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 8px 24px ${color}20` },
        }}>
            <Box sx={{
                width: 48, height: 48, borderRadius: '4px', display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                background: `${color}15`, color: color,
            }}>
                {icon}
            </Box>
            <Box>
                <Typography sx={{ fontSize: 28, fontWeight: 800, lineHeight: 1, color }}>
                    {value}
                </Typography>
                <Typography sx={{ fontSize: 12, color: 'text.secondary', fontWeight: 500, mt: '2px' }}>
                    {label}
                </Typography>
                {subtitle && (
                    <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>
                        {subtitle}
                    </Typography>
                )}
            </Box>
        </Paper>
    );

    return (
        <Box sx={{ minHeight: 'calc(100vh - 120px)' }}>
            {/* Header premium */}
            <Paper sx={{
                background: `linear-gradient(135deg, ${PF.primary} 0%, ${PF.primaryLight} 100%)`,
                borderRadius: '4px', p: '20px 24px', mb: '16px', color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <Box sx={{
                        width: 48, height: 48, borderRadius: '4px',
                        background: 'rgba(255,255,255,0.15)', display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                    }}>
                        <DrawIcon sx={{ fontSize: 28 }} />
                    </Box>
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: '-0.5px' }}>
                            Panel de Firma
                        </Typography>
                        <Typography sx={{ fontSize: 13, opacity: 0.85 }}>
                            {acceso.esFiscal ? 'Fiscal del Estado' : 'Delegado autorizado'}
                            {acceso.fiscalNombre && ` — ${acceso.fiscalNombre}`}
                        </Typography>
                    </Box>
                </Box>

                {/* Resumen rápido en el header */}
                {pendientes > 0 && (
                    <Box sx={{
                        display: 'flex', alignItems: 'center', gap: '8px',
                        background: 'rgba(255,255,255,0.12)', borderRadius: '8px', p: '8px 16px',
                    }}>
                        <WarningIcon sx={{ fontSize: 20, color: PF.gold }} />
                        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                            {pendientes} documento{pendientes !== 1 ? 's' : ''} pendiente{pendientes !== 1 ? 's' : ''} de firma
                        </Typography>
                    </Box>
                )}

                <Tooltip title="Actualizar">
                    <IconButton onClick={() => { refreshStats(); }} sx={{ color: '#fff' }}>
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            </Paper>

            {/* Dashboard stats */}
            <Box sx={{ display: 'flex', gap: '12px', mb: '16px', flexWrap: 'wrap' }}>
                <StatCard
                    icon={<ScheduleIcon sx={{ fontSize: 26 }} />}
                    label="Pendientes"
                    value={pendientes}
                    color={pendientes > 0 ? '#f57c00' : '#9e9e9e'}
                    subtitle={`CP: ${stats?.pendientesCartasPoder ?? 0} | Readj: ${stats?.pendientesReadjudicacion ?? 0}`}
                />
                <StatCard
                    icon={<CheckIcon sx={{ fontSize: 26 }} />}
                    label="Firmados hoy"
                    value={firmadosHoy}
                    color="#2e7d32"
                    subtitle={`CP: ${stats?.firmadosHoyCartasPoder ?? 0} | Readj: ${stats?.firmadosHoyReadjudicacion ?? 0}`}
                />
                <StatCard
                    icon={<CancelIcon sx={{ fontSize: 26 }} />}
                    label="Rechazados hoy"
                    value={rechazadosHoy}
                    color="#c62828"
                />
                <StatCard
                    icon={<TrendingUpIcon sx={{ fontSize: 26 }} />}
                    label="Procesados hoy"
                    value={totalHoy}
                    color={PF.accent}
                />
            </Box>

            {/* Tabs */}
            <Paper sx={{ borderRadius: '4px', mb: '16px', overflow: 'hidden' }}>
                <Tabs
                    value={tabActual}
                    onChange={(_, v) => setTabActual(v)}
                    sx={{
                        '& .MuiTabs-indicator': { backgroundColor: PF.primary, height: 3 },
                        '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: 14 },
                        '& .Mui-selected': { color: `${PF.primary} !important` },
                    }}
                >
                    <Tab
                        icon={<Badge badgeContent={stats?.pendientesCartasPoder ?? 0} color="warning"><DescriptionIcon /></Badge>}
                        iconPosition="start"
                        label="Cartas Poder"
                    />
                    <Tab
                        icon={<Badge badgeContent={stats?.pendientesReadjudicacion ?? 0} color="warning"><GavelIcon /></Badge>}
                        iconPosition="start"
                        label="Readjudicación"
                    />
                    <Tab
                        icon={<Badge badgeContent={stats?.pendientesCargaMasiva ?? 0} color="warning"><UploadFileIcon /></Badge>}
                        iconPosition="start"
                        label="Carga Masiva"
                    />
                    {acceso.esFiscal && (
                        <Tab
                            icon={<PeopleIcon />}
                            iconPosition="start"
                            label="Delegados"
                        />
                    )}
                    <Tab
                        icon={<HistoryIcon />}
                        iconPosition="start"
                        label="Historial"
                    />
                </Tabs>
            </Paper>

            {/* Tab Content */}
            <Box>
                {tabActual === 0 && (
                    <CartasPoder acceso={acceso} onRefreshStats={refreshStats} />
                )}
                {tabActual === 1 && (
                    <FirmaCartasPoder onRefreshStats={refreshStats} />
                )}
                {tabActual === 2 && (
                    <FirmaCargaMasiva />
                )}
                {tabActual === 3 && acceso.esFiscal && (
                    <Delegados />
                )}
                {tabActual === (acceso.esFiscal ? 4 : 3) && (
                    <HistorialFirma />
                )}
            </Box>
        </Box>
    );
};

// Componente de Historial inline
const HistorialFirma = () => {
    const [historial, setHistorial] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busqueda, setBusqueda] = useState('');

    useEffect(() => {
        const cargar = async () => {
            try {
                const token = localStorage.getItem('SIGIF_JWT_TOKEN');
                const resp = await fetch(nodeApiPath('panel-firma/historial'), {
                    headers: { 'Authorization': `Bearer ${token}` },
                });
                const data = await resp.json();
                if (data?.ok) setHistorial(data.historial || []);
            } catch { /* ignore */ }
            setLoading(false);
        };
        cargar();
    }, []);

    const filtrado = busqueda.trim()
        ? historial.filter(h =>
            (h.Descripcion || '').toLowerCase().includes(busqueda.toLowerCase()) ||
            (h.Accion || '').toLowerCase().includes(busqueda.toLowerCase()) ||
            (h.Usuario || '').toLowerCase().includes(busqueda.toLowerCase())
        )
        : historial;

    const formatFecha = (f) => {
        if (!f) return '-';
        try {
            const d = new Date(f);
            return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
                ' ' + d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', hour12: false });
        } catch { return f; }
    };

    const accionColor = (accion) => {
        if (accion === 'APROBADO' || accion === 'FIRMADO' || accion === 'PDF_FIRMADO_SUBIDO') return '#2e7d32';
        if (accion === 'RECHAZADO') return '#c62828';
        if (accion === 'SOLICITADO') return '#f57c00';
        if (accion === 'PDF_SUBIDO') return '#1769aa';
        return '#546e7a';
    };

    const accionLabel = (accion) => {
        const labels = {
            'SOLICITADO': 'Solicitado',
            'APROBADO': 'Aprobado',
            'FIRMADO': 'Firmado',
            'RECHAZADO': 'Rechazado',
            'PDF_SUBIDO': 'PDF subido',
            'PDF_FIRMADO_SUBIDO': 'Firmado digitalmente',
        };
        return labels[accion] || accion;
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: '32px' }}><CircularProgress /></Box>;

    return (
        <Paper sx={{ borderRadius: '4px', overflow: 'hidden' }}>
            <Box sx={{ p: '12px 16px', display: 'flex', alignItems: 'center', gap: '12px', borderBottom: '1px solid #e0e0e0' }}>
                <HistoryIcon sx={{ color: PF.primary }} />
                <Typography sx={{ fontWeight: 600, flex: 1 }}>Historial de Firma</Typography>
                <TextField
                    size="small"
                    placeholder="Buscar..."
                    value={busqueda}
                    onChange={e => setBusqueda(e.target.value)}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                    }}
                    sx={{ width: 250, '& .MuiOutlinedInput-root': { borderRadius: '8px', height: 36 } }}
                />
            </Box>
            <Box sx={{ maxHeight: 500, overflow: 'auto' }}>
                {filtrado.length === 0 ? (
                    <Typography sx={{ p: '32px', textAlign: 'center', color: 'text.secondary' }}>
                        {busqueda ? 'Sin resultados' : 'No hay historial aún'}
                    </Typography>
                ) : (
                    filtrado.map((h, i) => (
                        <Box key={h.Id || i} sx={{
                            display: 'flex', alignItems: 'center', gap: '12px', p: '10px 16px',
                            borderBottom: '1px solid #f0f0f0',
                            '&:hover': { background: '#fafafa' },
                        }}>
                            <Box sx={{
                                width: 8, height: 8, borderRadius: '50%',
                                background: accionColor(h.Accion), flexShrink: 0,
                            }} />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{ fontSize: 13, fontWeight: 600 }} noWrap>
                                    {h.Detalle || h.Descripcion || `Documento #${h.Id_Documento}`}
                                </Typography>
                                <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                                    {accionLabel(h.Accion)} por {h.Usuario}
                                </Typography>
                            </Box>
                            <Typography sx={{ fontSize: 12, color: 'text.disabled', whiteSpace: 'nowrap' }}>
                                {formatFecha(h.Fecha)}
                            </Typography>
                        </Box>
                    ))
                )}
            </Box>
        </Paper>
    );
};

export default PanelFirma;
